import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { Row, Col, Alert, Card, CardBody, Container, FormFeedback, Input, Label, Form, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import { requestPasswordReset, verifyMFA, resetPassword } from "../../store/auth/forgetpwd/actions";
import profile from "../../assets/images/profile-img.png";
import logo from "../../assets/images/logo.svg";

const ForgotPasswordWithMFA = () => {
  document.title = "Forget Password | Sunwave";
  const apiUrl = process.env.REACT_APP_API_URL || "";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Step management and error handling
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Toggle password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Retrieve messages from Redux for backward compatibility
  const { resetError, resetSuccessMsg } = useSelector((state) => state.ForgetPassword);

  // Debug logging for troubleshooting
  useEffect(() => {
    console.log("Current step:", step);
    console.log("Email:", email);
    console.log("Loading state:", loading);
    console.log("Error state:", error);
    console.log("Success message:", successMessage);
  }, [step, email, loading, error, successMessage]);

  // Handle redirection after successful reset
  useEffect(() => {
    if (showSuccess) {
      setTimeout(() => navigate("/login"), 3000);
    }
  }, [showSuccess, navigate]);

  // Direct API calls using fetch instead of Redux
  const sendPasswordResetRequest = async (emailAddress) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${apiUrl}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailAddress }),
      });
      
      const data = await response.json();
      console.log("API Response for reset request:", data);
      
      if (response.ok) {
        setSuccessMessage(data.message || "Password reset code sent to your email.");
        setEmail(emailAddress);
        setStep(2); // Directly advance to next step on success
      } else {
        setError(data.error || "Failed to send reset code. Please try again.");
      }
    } catch (err) {
      console.error("Error in reset request:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyMFACode = async (emailAddress, mfaCode) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${apiUrl}/api/auth/verify-mfa-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: emailAddress, 
          code: mfaCode 
        }),
      });
      
      const data = await response.json();
      console.log("API Response for code verification:", data);
      
      if (response.ok) {
        setSuccessMessage(data.message || "Code verified successfully.");
        setCode(mfaCode);
        setStep(3); // Directly advance to next step on success
      } else {
        setError(data.error || "Invalid or expired code. Please try again.");
      }
    } catch (err) {
      console.error("Error in code verification:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetUserPassword = async (emailAddress, newPassword) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${apiUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: emailAddress, 
          newPassword: newPassword 
        }),
      });
      
      const data = await response.json();
      console.log("API Response for password reset:", data);
      
      if (response.ok) {
        setSuccessMessage(data.message || "Password reset successful!");
        setShowSuccess(true);
      } else {
        setError(data.error || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      console.error("Error in password reset:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Form validation schemas
  const validationSchemas = [
    Yup.object({ 
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email is required") 
    }),
    Yup.object({ 
      code: Yup.string()
        .matches(/^\d{6}$/, "Code must be exactly 6 digits")
        .required("Verification code is required") 
    }),
    Yup.object({ 
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required("Please confirm your password")
    }),
  ];

  // Initialize formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { 
      email: email || "", 
      code: code || "", 
      password: "",
      confirmPassword: "" 
    },
    validationSchema: validationSchemas[step - 1],
    onSubmit: (values) => {
      if (step === 1) {
        sendPasswordResetRequest(values.email);
      } else if (step === 2) {
        verifyMFACode(email, values.code);
      } else if (step === 3) {
        resetUserPassword(email, values.password);
      }
    },
  });

  // Handle going back to previous step
  const handleBack = () => {
    setStep(prevStep => Math.max(1, prevStep - 1));
    setError("");
    setSuccessMessage("");
  };

  // Handle resending verification code
  const handleResendCode = (e) => {
    e.preventDefault();
    sendPasswordResetRequest(email);
  };

  return (
    <React.Fragment>
      <div className="home-btn d-none d-sm-block">
        <Link to="/" className="text-dark"><i className="bx bx-home h2" /></Link>
      </div>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row>
                    <Col xs={7}>
                      <div className="text-primary p-4">
                        <h5 className="text-primary">Reset Password</h5>
                        <p className="text-primary mb-0">
                          {step === 1 && "Enter your email to receive a verification code"}
                          {step === 2 && "Enter the 6-digit code sent to your email"}
                          {step === 3 && "Create your new secure password"}
                        </p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profile} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div>
                    <Link to="/">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img src={logo} alt="" className="rounded-circle" height="34" />
                        </span>
                      </div>
                    </Link>
                  </div>
                  
                  <div className="p-2">
                    {/* Step indicator */}
                    <div className="steps-container mb-4">
                      <div className="d-flex justify-content-between">
                        <div className={`step-item ${step >= 1 ? "active" : ""}`}>
                          <div className="step-number">1</div>
                          <div className="step-label">Email</div>
                        </div>
                        <div className={`step-line ${step >= 2 ? "active" : ""}`}></div>
                        <div className={`step-item ${step >= 2 ? "active" : ""}`}>
                          <div className="step-number">2</div>
                          <div className="step-label">Verify</div>
                        </div>
                        <div className={`step-line ${step >= 3 ? "active" : ""}`}></div>
                        <div className={`step-item ${step >= 3 ? "active" : ""}`}>
                          <div className="step-number">3</div>
                          <div className="step-label">Reset</div>
                        </div>
                      </div>
                    </div>

                    {/* Status messages */}
                    {error && <Alert color="danger">{error}</Alert>}
                    {loading && <Alert color="info">Processing your request...</Alert>}
                    {showSuccess && <Alert color="success">
                      {successMessage}
                      <div className="mt-2">Redirecting to login page...</div>
                    </Alert>}
                    {!loading && step === 2 && !error && <Alert color="success">
                      Verification code has been sent to {email}
                    </Alert>}

                    {/* Password Reset Form */}
                    {!showSuccess && (
                      <Form className="form-horizontal" onSubmit={formik.handleSubmit}>
                        {/* Step 1: Email Input */}
                        {step === 1 && (
                          <div className="mb-3">
                            <Label className="form-label">Email Address</Label>
                            <Input
                              name="email"
                              type="email"
                              placeholder="Enter your email address"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.email}
                              invalid={formik.touched.email && !!formik.errors.email}
                              disabled={loading}
                            />
                            {formik.touched.email && formik.errors.email && (
                              <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
                            )}
                          </div>
                        )}

                        {/* Step 2: Verification Code Input */}
                        {step === 2 && (
                          <>
                            <div className="mb-3">
                              <Label className="form-label">Verification Code</Label>
                              <Input
                                name="code"
                                type="text"
                                placeholder="Enter the 6-digit code"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.code}
                                invalid={formik.touched.code && !!formik.errors.code}
                                disabled={loading}
                              />
                              {formik.touched.code && formik.errors.code && (
                                <FormFeedback type="invalid">{formik.errors.code}</FormFeedback>
                              )}
                            </div>
                            <div className="mt-2 mb-3 text-center">
                              <p className="text-muted mb-0">
                                Didn't receive the code?{" "}
                                <a href="#" onClick={handleResendCode} className="fw-semibold">
                                  Resend Code
                                </a>
                              </p>
                            </div>
                          </>
                        )}

                        {/* Step 3: New Password Input */}
                        {step === 3 && (
                          <>
                            <div className="mb-3">
                              <Label className="form-label">New Password</Label>
                              <div className="input-group">
                                <Input
                                  name="password"
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Enter your new password"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.password}
                                  invalid={formik.touched.password && !!formik.errors.password}
                                  disabled={loading}
                                />
                                <Button
                                  color="link"
                                  className="btn-sm"
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? "Hide" : "Show"}
                                </Button>
                                {formik.touched.password && formik.errors.password && (
                                  <FormFeedback type="invalid">{formik.errors.password}</FormFeedback>
                                )}
                              </div>
                            </div>

                            <div className="mb-3">
                              <Label className="form-label">Confirm Password</Label>
                              <div className="input-group">
                                <Input
                                  name="confirmPassword"
                                  type={showConfirmPassword ? "text" : "password"}
                                  placeholder="Confirm your new password"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.confirmPassword}
                                  invalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                                  disabled={loading}
                                />
                                <Button
                                  color="link"
                                  className="btn-sm"
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                  {showConfirmPassword ? "Hide" : "Show"}
                                </Button>
                                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                                  <FormFeedback type="invalid">{formik.errors.confirmPassword}</FormFeedback>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        {/* Form Actions */}
                        <div className="d-flex justify-content-between mt-4">
                          {step > 1 ? (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={handleBack}
                              disabled={loading}
                            >
                              Back
                            </button>
                          ) : (
                            <div></div> // Empty div for flex layout
                          )}
                          
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading ? (
                              <span>
                                <i className="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                                Processing...
                              </span>
                            ) : (
                              step === 1 ? "Send Code" : step === 2 ? "Verify Code" : "Reset Password"
                            )}
                          </button>
                        </div>
                      </Form>
                    )}
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>Remember your password? <Link to="/login" className="fw-medium text-primary">Login</Link></p>
                <p>© {new Date().getFullYear()} Sunwave Portal. Crafted with <i className="mdi mdi-heart text-danger" /> by IronGate</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Styling for step indicators */}
      <style jsx="true">{`
        .steps-container {
          margin: 20px 0;
        }
        
        .step-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 60px;
          position: relative;
          z-index: 2;
        }
        
        .step-number {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background-color: #f8f9fa;
          border: 2px solid #ced4da;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin-bottom: 5px;
          transition: all 0.3s;
        }
        
        .step-label {
          font-size: 12px;
          color: #74788d;
          transition: all 0.3s;
        }
        
        .step-line {
          height: 2px;
          background-color: #ced4da;
          flex-grow: 1;
          margin-top: 15px;
          position: relative;
          z-index: 1;
        }
        
        .step-line.active {
          background-color: #556ee6;
        }
        
        .step-item.active .step-number {
          background-color: #556ee6;
          border-color: #556ee6;
          color: white;
        }
        
        .step-item.active .step-label {
          color: #556ee6;
          font-weight: bold;
        }
      `}</style>
    </React.Fragment>
  );
};

ForgotPasswordWithMFA.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ForgotPasswordWithMFA);